import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

type AudioPlayerProps = React.DetailedHTMLProps<
  React.AudioHTMLAttributes<HTMLAudioElement>,
  HTMLAudioElement
> & {
  render: (props: {
    onClick: (
      e: React.MouseEvent<HTMLElement>,
      trackUrl?: string,
      trackKey?: string,
    ) => void
    loading: boolean
    currentTrackUrl?: string
    currentTrackKey?: string
    isPlaying: boolean
    currentTime: number
    duration: number
    onSeek: (timeInSeconds: number) => void
  }) => JSX.Element
  onTrackEnd?: (durationMs: number) => void
  isActive?: boolean
}

export const TrackAudioPlayer: FC<AudioPlayerProps> = ({
  render,
  onTrackEnd,
  isActive = true,
  ...restProps
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const playRequestRef = useRef(0)
  const playedTrackKeyRef = useRef('')
  const [currentTrackUrl, setCurrentTrackUrl] = useState('')
  const [currentTrackKey, setCurrentTrackKey] = useState('')
  const [isPlaying, setIsPlaying] = useState(false)
  const [prevIsActive, setPrevIsActive] = useState(isActive)

  if (prevIsActive !== isActive) {
    setPrevIsActive(isActive)
    if (!isActive) {
      setIsPlaying(false)
    }
  }
  const [isLoading, setIsLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [seekTarget, setSeekTarget] = useState<{ time: number } | null>(null)

  useEffect(() => {
    const ap = audioRef.current

    if (!ap) {
      return
    }

    if (!isPlaying || !currentTrackUrl) {
      ap.pause()

      return
    }

    const currentAudioSrc = ap.currentSrc || ap.src
    const isNewSource = currentAudioSrc !== currentTrackUrl

    if (isNewSource) {
      ap.src = currentTrackUrl
      ap.load()
    }

    if (!isNewSource && playedTrackKeyRef.current !== currentTrackKey) {
      ap.currentTime = 0
    }

    playedTrackKeyRef.current = currentTrackKey
    playRequestRef.current += 1
    const playRequest = playRequestRef.current

    void ap.play()?.catch(() => {
      if (playRequest !== playRequestRef.current) {
        return
      }

      setIsLoading(false)
      setIsPlaying(false)
    })
  }, [currentTrackUrl, currentTrackKey, isPlaying])

  useEffect(() => {
    const ap = audioRef.current

    if (seekTarget !== null && ap) {
      ap.currentTime = seekTarget.time
    }
  }, [seekTarget])

  const handleAudioEnd = (e: React.SyntheticEvent<HTMLAudioElement>): void => {
    setIsPlaying(false)
    setIsLoading(false)
    const d = e.currentTarget.duration
    if (!Number.isNaN(d)) {
      onTrackEnd?.(Math.round(d * 1000))
    }
  }

  const handleAudioLoadStart = (): void => {
    setIsLoading(true)
  }

  const handleAudioCanPlay = (): void => {
    setIsLoading(false)
  }

  const handleAudioError = (): void => {
    setIsLoading(false)
    setIsPlaying(false)
  }

  const handleTimeUpdate = (
    e: React.SyntheticEvent<HTMLAudioElement>,
  ): void => {
    setCurrentTime(Math.round(e.currentTarget.currentTime * 1000))
  }

  const handleLoadedMetadata = (
    e: React.SyntheticEvent<HTMLAudioElement>,
  ): void => {
    const d = e.currentTarget.duration
    setDuration(Number.isNaN(d) ? 0 : Math.round(d * 1000))
  }

  const handleSeek = useCallback((timeInSeconds: number): void => {
    setSeekTarget({ time: timeInSeconds })
  }, [])

  const handleClickCover = useCallback(
    (
      e: React.MouseEvent<HTMLElement>,
      trackUrl?: string,
      trackKey?: string,
    ): void => {
      e.stopPropagation()

      if (!trackUrl) {
        return
      }

      const nextTrackKey = trackKey ?? trackUrl

      if (currentTrackKey !== nextTrackKey || !isPlaying) {
        setCurrentTrackKey(nextTrackKey)
        setCurrentTrackUrl(trackUrl)
        setIsPlaying(true)

        return
      }

      setIsPlaying(false)
    },
    [currentTrackKey, isPlaying],
  )

  return (
    <>
      <audio
        {...restProps}
        ref={audioRef}
        onLoadStart={handleAudioLoadStart}
        onCanPlay={handleAudioCanPlay}
        onPlaying={handleAudioCanPlay}
        onEnded={handleAudioEnd}
        onError={handleAudioError}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
      />

      {render({
        currentTrackUrl,
        currentTrackKey,
        isPlaying,
        currentTime,
        duration,
        onSeek: handleSeek,
        loading: isLoading,
        onClick: handleClickCover,
      })}
    </>
  )
}
